
import { createContext, useState } from 'react'

import { api } from 'utils/api'
import { getError } from 'utils/helpers/getError'
import { useCustomContext } from 'utils/helpers/useCustomContext'
import { GenericResponse, GenericStateType } from 'utils/types'

import { EngineContextType, EngineFormType, EngineResponseType, EngineStateType } from './engineTypes'

const INITIAL_ENGINE_CONTEXT: EngineContextType = {
  setLoading: () => null,
  setError: () => null,
  clearError: () => null,
  clearEngineMessage: () => null,
  getEngineStatus: () => null,
  lockEngine: () => new Promise(() => null),
  unlockEngine: () => new Promise(() => null),
  engineState: {
    error: '',
    loading: false,
    state: {
      engineBlocked: null,
      lockEngineMessage: '',
    },
  },
}

const EngineContext = createContext<EngineContextType>(INITIAL_ENGINE_CONTEXT)

const EngineProvider: React.FC = ({ children }) => {
  const [engineState, setEngineState] = useState<GenericStateType<EngineStateType>>(INITIAL_ENGINE_CONTEXT.engineState)

  const setLoading = (value = true) =>
    setEngineState(prevState => ({ ...prevState, loading: value }))

  const setError = (msg: string) =>
    setEngineState(prevState => ({ ...prevState, loading: false, error: msg }))

  const clearError = () =>
    setEngineState(prevState => ({ ...prevState, error: '' }))

  const getEngineStatus = async (id: string) => {
    try {
      setLoading(true)
      engineState.error !== '' && clearError()

      const { data: { payload } } = await api.get<GenericResponse<EngineResponseType>>
        (`Vehicles/${id}/EngineLockStatus`)

      setEngineState(prevState => ({
        ...prevState,
        loading: false,
        state: {
          ...prevState.state,
          engineBlocked: payload.engineBlocked,
        },
      }))

      setLoading(false)
    } catch (err) {
      const message = getError(err)

      setError(message)
    }
  }

  const lockEngine = async (data: EngineFormType, vehicleId: string) => {
    try {
      setLoading()
      engineState.error !== '' && clearError()

      await api.post<GenericResponse<boolean>>(`Vehicles/${vehicleId}/Lock`, data)

      setEngineState(prevState => ({
        ...prevState,
        loading: false,
        state: {
          ...prevState.state,
          lockEngineMessage: 'Motor bloqueado con éxito',
          engineBlocked: true,
        },
      }))
      setLoading(false)

    } catch (err) {
      const message = getError(err)

      setError(message)
    }
  }

  const unlockEngine = async (data: EngineFormType, vehicleId: string) => {
    try {
      setLoading()
      engineState.error !== '' && clearError()

      await api.post<GenericResponse<boolean>>(`Vehicles/${vehicleId}/Unlock`, data)

      setEngineState(prevState => ({
        ...prevState,
        loading: false,
        state: {
          ...prevState.state,
          lockEngineMessage: 'Motor desbloqueado con éxito',
          engineBlocked: false,
        },
      }))
      setLoading(false)

    } catch (err) {
      const message = getError(err)

      setError(message)
    }
  }

  const clearEngineMessage = () =>
    setEngineState(prevState => ({
      ...prevState,
      loading: false,
      state: {
        ...prevState.state,
        lockEngineMessage: '',
      },
    }))

  return (
    <EngineContext.Provider
      value={{
        engineState,
        setLoading,
        setError,
        clearError,
        getEngineStatus,
        lockEngine,
        unlockEngine,
        clearEngineMessage,
      }}>
      {children}
    </EngineContext.Provider>
  )
}

const useEngine = () => useCustomContext<EngineContextType>(EngineContext)

export { EngineProvider, useEngine }
